import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import emailjs from '@emailjs/browser';
import { 
  FileText, Upload, AlertCircle, X, User, Star, 
  CheckCircle, Loader2, AlertTriangle, Trash2, FileUp 
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen';

// COSTANTI
const ADMIN_EMAIL = 'info@tusciahub.it';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

// CREDENZIALI EMAILJS
const EMAIL_SERVICE_ID = 'service_vbsf3wg';
const EMAIL_TEMPLATE_ID = 'template_zkmoyqq';
const EMAIL_PUBLIC_KEY = '3ftqCgeBFXN0rqFE9';

// LIMITI TESTO
const MAX_TITLE_LENGTH = 100;
const MAX_DESC_LENGTH = 1000;
const MAX_PROF_LENGTH = 50;
const MAX_QUESTION_LENGTH = 500;
const MAX_COMMENT_LENGTH = 500;

const Subject = () => {
  const { deptId, courseId, subjectName } = useParams();
  const decodedSubject = decodeURIComponent(subjectName);
  
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('notes'); 
  const [data, setData] = useState({ notes: [], questions: [], reviews: [] });
  const [modals, setModals] = useState({ upload: false, question: false, review: false });
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState(false);

  const generateAcademicYears = () => {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = 0; i < 5; i++) {
          years.push(`${currentYear - i}/${currentYear - i + 1}`);
      }
      return years;
  };
  const academicYearsOptions = generateAcademicYears();

  const [forms, setForms] = useState({
    upload: { title: '', description: '', professor: '', academic_year: '', price: '', is_negotiable: false, file: null, termsAccepted: false },
    question: '',
    review: { rating: 5, comment: '' }
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
      
      // LOGICA REALE ADMIN (RIPRISTINATA)
      setIsAdmin(user?.email === ADMIN_EMAIL); 

      // QUERY: Includiamo profiles(email) per poter inviare le notifiche
      const [notesRes, questionsRes, reviewsRes] = await Promise.all([
        supabase.from('notes').select('*, profiles(email)').eq('subject', decodedSubject).order('created_at', { ascending: false }),
        supabase.from('exam_questions').select('*, profiles(email)').eq('subject_id', decodedSubject).order('created_at', { ascending: false }),
        supabase.from('reviews').select('*, profiles(email)').eq('subject_id', decodedSubject).is('note_id', null).order('created_at', { ascending: false })
      ]);

      setData({
        notes: notesRes.data || [],
        questions: questionsRes.data || [],
        reviews: reviewsRes.data || []
      });

    } catch (error) {
      console.error("Errore fetch:", error);
    } finally {
      setInitialLoading(false);
    }
  }, [decodedSubject]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const fileExt = file.name.split('.').pop().toLowerCase();
      if (fileExt !== 'pdf') { showToast("Solo PDF.", "error"); e.target.value = null; return; }
      if (file.size > MAX_FILE_SIZE_BYTES) { setFileError(true); setForms(prev => ({...prev, upload: {...prev.upload, file: null}})); e.target.value = null; } 
      else { setFileError(false); setForms(prev => ({...prev, upload: {...prev.upload, file: file}})); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!currentUser) return showToast("Devi accedere!", "error");
    if (!forms.upload.termsAccepted) return showToast("Accetta i termini.", "error");
    if (fileError) return showToast("File troppo grande.", "error");

    setIsSubmitting(true);
    const tempId = Date.now();
    const numericPrice = forms.upload.price ? parseFloat(forms.upload.price) : 0;

    const optimisticNote = {
        id: tempId, created_at: new Date().toISOString(), title: forms.upload.title, description: forms.upload.description,
        professor: forms.upload.professor, academic_year: forms.upload.academic_year, price: numericPrice, is_negotiable: forms.upload.is_negotiable,
        subject: decodedSubject, user_id: currentUser.id, is_approved: false, profiles: { email: currentUser.email }, file_url: '#', isOptimistic: true 
    };

    setData(prev => ({ ...prev, notes: [optimisticNote, ...prev.notes] }));
    setModals({ ...modals, upload: false });
    setActiveTab('notes');
    showToast("Caricamento...", "success");

    try {
        const file = forms.upload.file;
        const fileExt = file.name.split('.').pop();
        const fileName = `${currentUser.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage.from('appunti').upload(fileName, file);
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage.from('appunti').getPublicUrl(fileName);

        const { data: realNote, error: dbError } = await supabase.from('notes').insert([{
            title: forms.upload.title, description: forms.upload.description, professor: forms.upload.professor,
            academic_year: forms.upload.academic_year, price: numericPrice, is_negotiable: forms.upload.is_negotiable,
            file_url: publicUrl, subject: decodedSubject, dept_id: deptId, course_id: courseId, user_id: currentUser.id, is_approved: false
        }]).select('*, profiles(email)').single();

        if (dbError) throw dbError;
        
        setData(prev => ({ ...prev, notes: prev.notes.map(n => n.id === tempId ? realNote : n) }));
        showToast("Dispensa pubblicata!", "success");
        setForms(prev => ({ ...prev, upload: { title: '', description: '', professor: '', academic_year: '', price: '', is_negotiable: false, file: null, termsAccepted: false } }));
        setFileError(false);
    } catch (error) {
        console.error(error);
        setData(prev => ({ ...prev, notes: prev.notes.filter(n => n.id !== tempId) }));
        showToast("Errore upload.", "error");
    } finally { setIsSubmitting(false); }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return showToast("Accedi prima!", "error");
    setIsSubmitting(true);
    const tempId = Date.now();
    const optimisticReview = { id: tempId, created_at: new Date().toISOString(), rating: forms.review.rating, comment: forms.review.comment, subject_id: decodedSubject, user_id: currentUser.id, is_approved: false, profiles: { email: currentUser.email }, isOptimistic: true };
    setData(prev => ({ ...prev, reviews: [optimisticReview, ...prev.reviews] }));
    setModals({ ...modals, review: false });
    setActiveTab('reviews');
    showToast("Inviata!", "success");
    try {
        const { data: realReview, error } = await supabase.from('reviews').insert([{ rating: forms.review.rating, comment: forms.review.comment, subject_id: decodedSubject, note_id: null, user_id: currentUser.id, is_approved: false }]).select('*, profiles(email)').single();
        if (error) throw error;
        setData(prev => ({ ...prev, reviews: prev.reviews.map(r => r.id === tempId ? realReview : r) }));
        setForms(prev => ({ ...prev, review: { rating: 5, comment: '' } }));
    } catch (error) { setData(prev => ({ ...prev, reviews: prev.reviews.filter(r => r.id !== tempId) })); showToast("Errore invio.", "error"); } finally { setIsSubmitting(false); }
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) return showToast("Accedi prima!", "error");
    setIsSubmitting(true);
    const tempId = Date.now();
    const optimisticQ = { id: tempId, created_at: new Date().toISOString(), question_text: forms.question, subject_id: decodedSubject, user_id: currentUser.id, is_approved: false, profiles: { email: currentUser.email }, isOptimistic: true };
    setData(prev => ({ ...prev, questions: [optimisticQ, ...prev.questions] }));
    setModals({ ...modals, question: false });
    setActiveTab('questions');
    showToast("Inviata!", "success");
    try {
        const { data: realQ, error } = await supabase.from('exam_questions').insert([{ question_text: forms.question, subject_id: decodedSubject, user_id: currentUser.id, is_approved: false }]).select('*, profiles(email)').single();
        if (error) throw error;
        setData(prev => ({ ...prev, questions: prev.questions.map(q => q.id === tempId ? realQ : q) }));
        setForms(prev => ({ ...prev, question: '' }));
    } catch (error) { setData(prev => ({ ...prev, questions: prev.questions.filter(q => q.id !== tempId) })); showToast("Errore invio.", "error"); } finally { setIsSubmitting(false); }
  };

  // --- AZIONI (APPROVA / ELIMINA) ---
  const handleAction = async (table, id, action) => {
    if (action === 'delete' && !window.confirm("Sicuro di eliminare?")) return;
    
    const stateKey = table === 'notes' ? 'notes' : table === 'exam_questions' ? 'questions' : 'reviews';
    const previousData = [...data[stateKey]];
    const itemTarget = data[stateKey].find(item => item.id === id); 

    if (action === 'delete') {
        setData(prev => ({ ...prev, [stateKey]: prev[stateKey].filter(item => item.id !== id) }));
    } else if (action === 'approve') {
        setData(prev => ({ ...prev, [stateKey]: prev[stateKey].map(item => item.id === id ? { ...item, is_approved: true } : item) }));
    }

    try {
      let error = null;
      if (action === 'approve') {
        const res = await supabase.from(table).update({ is_approved: true }).eq('id', id);
        error = res.error;
        
        if (!error) {
            showToast("Approvato!", "success");

            if (table === 'notes') {
                const userEmail = itemTarget?.profiles?.email;
                
                if (userEmail) {
                    // Invio Email Silenzioso
                    const emailParams = {
                        to_email: userEmail,
                        to_name: userEmail.split('@')[0],
                        note_title: itemTarget.title,
                        subject_name: decodedSubject,
                        message: "La tua dispensa è stata approvata ed è ora visibile a tutti!"
                    };

                    emailjs.send(EMAIL_SERVICE_ID, EMAIL_TEMPLATE_ID, emailParams, EMAIL_PUBLIC_KEY)
                        .then((r) => console.log("Email inviata:", r.text))
                        .catch((e) => console.error("Errore EmailJS:", e));
                } else {
                    console.warn("Nessuna email trovata per questo utente.");
                }
            }
        }
      } else if (action === 'delete') {
        if (table === 'notes' && itemTarget && itemTarget.file_url) {
            try {
                const fileUrlParts = itemTarget.file_url.split('/appunti/');
                if (fileUrlParts.length > 1) {
                    const filePath = fileUrlParts[1];
                    await supabase.storage.from('appunti').remove([filePath]);
                }
            } catch (err) { console.error("Errore URL:", err); }
        }
        const res = await supabase.from(table).delete().eq('id', id);
        error = res.error;
        if (!error) showToast("Eliminato.", "success");
      }
      if (error) throw error;
    } catch (error) {
      console.error("Errore Database:", error.message);
      showToast("Errore nel database.", "error");
      setData(prev => ({ ...prev, [stateKey]: previousData }));
    }
  };

  const shouldShowItem = (item) => item.is_approved || isAdmin || (currentUser && currentUser.id === item.user_id) || item.isOptimistic;
  const inputStyle = "w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-slate-700 placeholder:text-slate-400 font-medium outline-none";

  if (initialLoading) return <LoadingScreen />;

  return (
    <div className="relative min-h-screen bg-slate-50 pt-24 pb-20 px-6 font-sans text-slate-800">
      <SEO title={decodedSubject} description={`Materiale per ${decodedSubject}`} url={`/subject/${decodedSubject}`} />
      
      {toast && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[9999] animate-in slide-in-from-bottom-5 fade-in duration-300">
            <div className={`px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 border min-w-[300px] ${toast.type === 'error' ? 'bg-red-900 text-white border-red-700' : 'bg-slate-900 text-white border-slate-700'}`}>
                {toast.type === 'success' ? <CheckCircle className="text-green-400"/> : <AlertCircle className="text-red-400"/>}
                <span className="font-bold text-sm">{toast.message}</span>
            </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto mt-6">
        <PageHeader title={decodedSubject} subtitle="Materiale didattico, domande e recensioni del corso." breadcrumbs={[{ label: courseId ? courseId.toUpperCase() : 'Corso', link: `/course/${deptId}/${courseId}` }, { label: decodedSubject }]} />

        <div className="grid md:grid-cols-3 gap-4 mb-8">
            <ActionButton onClick={() => currentUser ? setModals({...modals, upload: true}) : showToast("Accedi prima!", "error")} icon={<Upload/>} label="Carica Dispensa" colorClass="bg-indigo-600 hover:bg-indigo-700 text-white" />
            <ActionButton onClick={() => currentUser ? setModals({...modals, question: true}) : showToast("Accedi prima!", "error")} icon={<AlertCircle className="text-red-500"/>} label="Segnala Domanda" colorClass="bg-white border-slate-200 text-slate-700" />
            <ActionButton onClick={() => currentUser ? setModals({...modals, review: true}) : showToast("Accedi prima!", "error")} icon={<Star className="text-yellow-500"/>} label="Recensisci Corso" colorClass="bg-white border-slate-200 text-slate-700" />
        </div>

        <div className="flex justify-center mb-8 border-b border-slate-200 overflow-x-auto no-scrollbar">
            <TabButton active={activeTab === 'notes'} onClick={() => setActiveTab('notes')} label={`Dispense (${data.notes.filter(shouldShowItem).length})`} color="indigo" />
            <TabButton active={activeTab === 'questions'} onClick={() => setActiveTab('questions')} label={`Domande (${data.questions.filter(shouldShowItem).length})`} color="red" />
            <TabButton active={activeTab === 'reviews'} onClick={() => setActiveTab('reviews')} label={`Recensioni Corso (${data.reviews.filter(shouldShowItem).length})`} color="yellow" />
        </div>

        <div className="min-h-[400px]">
            {activeTab === 'notes' && (
                <div className="space-y-4">
                    {data.notes.filter(shouldShowItem).map(note => (
                        <NoteCard key={note.id} note={note} isAdmin={isAdmin} currentUser={currentUser} onApprove={() => handleAction('notes', note.id, 'approve')} onDelete={() => handleAction('notes', note.id, 'delete')} />
                    ))}
                    {data.notes.filter(shouldShowItem).length === 0 && <EmptyState icon={FileText} message="Nessuna dispensa." actionLabel="Caricane una tu" onAction={() => setModals({...modals, upload: true})} />}
                </div>
            )}
            {activeTab === 'questions' && (
                <div className="space-y-4">
                    {data.questions.filter(shouldShowItem).map(q => (
                        <QuestionCard key={q.id} question={q} isAdmin={isAdmin} currentUser={currentUser} onApprove={() => handleAction('exam_questions', q.id, 'approve')} onDelete={() => handleAction('exam_questions', q.id, 'delete')} />
                    ))}
                    {data.questions.filter(shouldShowItem).length === 0 && <EmptyState icon={AlertCircle} message="Nessuna domanda." actionLabel="Segnala la prima" onAction={() => setModals({...modals, question: true})} />}
                </div>
            )}
            {activeTab === 'reviews' && (
                <div className="space-y-4">
                    {data.reviews.filter(shouldShowItem).map(r => (
                        <ReviewCard key={r.id} review={r} isAdmin={isAdmin} currentUser={currentUser} onApprove={() => handleAction('reviews', r.id, 'approve')} onDelete={() => handleAction('reviews', r.id, 'delete')} />
                    ))}
                    {data.reviews.filter(shouldShowItem).length === 0 && <EmptyState icon={Star} message="Nessuna recensione." actionLabel="Scrivi la prima" onAction={() => setModals({...modals, review: true})} />}
                </div>
            )}
        </div>
      </div>

      {modals.upload && (
        <Modal onClose={() => {setModals({...modals, upload: false}); setFileError(false);}} title="Pubblica Dispensa">
            <form onSubmit={handleUpload} className="space-y-4">
                <input type="text" required placeholder="Titolo Dispensa" maxLength={MAX_TITLE_LENGTH} className={inputStyle} value={forms.upload.title} onChange={e => setForms({...forms, upload: {...forms.upload, title: e.target.value}})} />
                <textarea required placeholder="Descrizione..." maxLength={MAX_DESC_LENGTH} className={`${inputStyle} h-28 resize-none`} value={forms.upload.description} onChange={e => setForms({...forms, upload: {...forms.upload, description: e.target.value}})}></textarea>
                <div className="flex gap-4">
                    <div className="relative flex-1">
                        <input type="number" placeholder="Prezzo (€)" min="0" step="0.01" className={inputStyle} value={forms.upload.price} onChange={e => setForms({...forms, upload: {...forms.upload, price: e.target.value}})} />
                    </div>
                    <label className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 cursor-pointer hover:bg-white hover:border-indigo-300 transition-all">
                        <input type="checkbox" className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600" checked={forms.upload.is_negotiable} onChange={e => setForms({...forms, upload: {...forms.upload, is_negotiable: e.target.checked}})} /> 
                        <span className="text-sm font-bold text-slate-600 uppercase tracking-wide">Trattabile</span>
                    </label>
                </div>
                <div className="flex gap-4">
                    <input type="text" placeholder="Professore" maxLength={MAX_PROF_LENGTH} className={inputStyle} value={forms.upload.professor} onChange={e => setForms({...forms, upload: {...forms.upload, professor: e.target.value}})} />
                    <select required className={`${inputStyle} cursor-pointer appearance-none`} value={forms.upload.academic_year} onChange={e => setForms({...forms, upload: {...forms.upload, academic_year: e.target.value}})}>
                        <option value="" disabled>Anno Acc.</option>
                        {academicYearsOptions.map(year => <option key={year} value={year}>{year}</option>)}
                        <option value="Oltre">Oltre (5+ anni)</option>
                    </select>
                </div>
                <div className="pt-2">
                    {fileError ? (
                        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center">
                            <h4 className="font-bold text-rose-700 mb-1">File troppo grande!</h4>
                            <div className="flex gap-2 mt-2">
                                <a href="https://www.ilovepdf.com/it/comprimere_pdf" target="_blank" rel="noreferrer" className="flex-1 bg-white border border-rose-200 text-rose-600 py-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 hover:bg-rose-50 transition shadow-sm">Comprimi PDF</a>
                                <button type="button" onClick={() => setFileError(false)} className="flex-1 bg-rose-600 text-white py-3 rounded-xl text-xs font-bold hover:bg-rose-700 transition shadow-sm">Riprova</button>
                            </div>
                        </div>
                    ) : (
                        <div className="relative group">
                            <input type="file" required id="file-upload" accept=".pdf" className="hidden" onChange={handleFileChange} />
                            <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center justify-center gap-2 h-36 w-full rounded-2xl border-2 border-dashed border-indigo-200 bg-indigo-50/20 hover:bg-indigo-50 hover:border-indigo-400 transition-all group-hover:scale-[1.01]">
                                {forms.upload.file ? (
                                    <>
                                        <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center shadow-sm"><CheckCircle size={24} strokeWidth={3}/></div>
                                        <span className="text-sm font-bold text-emerald-700 max-w-[80%] truncate">{forms.upload.file.name}</span>
                                    </>
                                ) : (
                                    <>
                                        <div className="w-12 h-12 bg-white text-indigo-500 rounded-2xl flex items-center justify-center shadow-sm border border-indigo-100"><FileUp size={24}/></div>
                                        <span className="text-sm font-bold text-slate-600">Carica file PDF (Max 10MB)</span>
                                    </>
                                )}
                            </label>
                        </div>
                    )}
                </div>
                <label className="flex items-center gap-3 p-2 cursor-pointer rounded-lg hover:bg-slate-50 transition-colors">
                    <input type="checkbox" required className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600" checked={forms.upload.termsAccepted} onChange={e => setForms({...forms, upload: {...forms.upload, termsAccepted: e.target.checked}})} />
                    <span className="text-xs text-slate-500 font-medium">Dichiaro di possedere i diritti e accetto i <Link to="/terms" className="text-indigo-600 font-bold hover:underline" target="_blank">Termini e Condizioni</Link>.</span>
                </label>
                <SubmitButton isSubmitting={isSubmitting} label="Pubblica (+50 Punti)" color="bg-indigo-600" />
            </form>
        </Modal>
      )}

      {modals.question && (
        <Modal onClose={() => setModals({...modals, question: false})} title="Nuova Domanda">
            <form onSubmit={handleQuestionSubmit} className="space-y-4">
                <textarea required placeholder="Scrivi qui la domanda d'esame..." maxLength={MAX_QUESTION_LENGTH} className={`${inputStyle} h-40 resize-none leading-relaxed`} value={forms.question} onChange={e => setForms({...forms, question: e.target.value})}></textarea>
                <SubmitButton isSubmitting={isSubmitting} label="Invia Domanda" color="bg-red-500" />
            </form>
        </Modal>
      )}
      
      {modals.review && (
        <Modal onClose={() => setModals({...modals, review: false})} title="Recensione Corso">
            <form onSubmit={handleReviewSubmit} className="space-y-5 text-center">
                <div className="flex justify-center gap-3">
                    {[1,2,3,4,5].map(s => (
                        <button key={s} type="button" onClick={() => setForms({...forms, review: {...forms.review, rating: s}})} className="transform hover:scale-110 transition-transform">
                            <Star size={36} className={s <= forms.review.rating ? "fill-yellow-400 text-yellow-400 drop-shadow-sm" : "text-slate-200"}/>
                        </button>
                    ))}
                </div>
                <textarea required placeholder="Racconta la tua esperienza..." maxLength={MAX_COMMENT_LENGTH} className={`${inputStyle} h-32 resize-none leading-relaxed`} value={forms.review.comment} onChange={e => setForms({...forms, review: {...forms.review, comment: e.target.value}})}></textarea>
                <SubmitButton isSubmitting={isSubmitting} label="Pubblica Recensione" color="bg-yellow-500" />
            </form>
        </Modal>
      )}
    </div>
  );
};

const Modal = ({onClose, title, children}) => (<div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto"><div className="bg-white w-full max-w-lg p-8 rounded-[2.5rem] shadow-2xl relative animate-in zoom-in-95 my-auto border border-white/40"><button onClick={onClose} className="absolute top-6 right-6 p-2 bg-slate-50 rounded-full hover:bg-slate-100 transition text-slate-400 hover:text-slate-600"><X size={20}/></button><h3 className="text-2xl font-black mb-8 text-slate-900 tracking-tight">{title}</h3>{children}</div></div>);
const ActionButton = ({onClick, icon, label, colorClass}) => <button onClick={onClick} className={`${colorClass} p-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-sm transition hover:-translate-y-1 active:scale-95`}>{icon} {label}</button>;
const TabButton = ({active, onClick, label, color}) => <button onClick={onClick} className={`pb-3 px-4 font-bold text-sm border-b-2 transition whitespace-nowrap ${active ? `border-${color}-500 text-${color}-600` : 'border-transparent text-slate-400 hover:text-slate-600'}`}>{label}</button>;
const SubmitButton = ({isSubmitting, label, color}) => <button disabled={isSubmitting} className={`w-full ${color} text-white py-4 rounded-2xl font-bold text-lg flex justify-center items-center gap-2 disabled:opacity-50 hover:opacity-90 transition shadow-xl hover:shadow-indigo-500/30 hover:-translate-y-0.5 active:scale-95`}>{isSubmitting ? <Loader2 className="animate-spin"/> : label}</button>;
const EmptyState = ({message, icon: Icon, actionLabel, onAction}) => <div className="text-center py-10 text-slate-400 flex flex-col items-center"><div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3"><Icon className="text-slate-300" size={32}/></div><p className="font-medium mb-1">{message}</p>{actionLabel && <button onClick={onAction} className="text-indigo-600 font-bold text-sm hover:underline">{actionLabel}</button>}</div>;

const NoteCard = ({note, isAdmin, currentUser, onApprove, onDelete}) => (
    <div className={`bg-white p-5 rounded-2xl border shadow-sm flex flex-col md:flex-row gap-4 transition hover:shadow-md ${note.isOptimistic ? 'opacity-70 animate-pulse' : ''} ${!note.is_approved ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100'}`}>
        <div className="bg-indigo-50 text-indigo-600 w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 border border-indigo-100"><FileText size={24}/></div>
        <div className="flex-grow">
            {(!note.is_approved || note.isOptimistic) && <div className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full mb-2 border border-amber-200">{note.isOptimistic ? <Loader2 className="animate-spin" size={10}/> : <AlertTriangle size={10}/>} {note.isOptimistic ? "Invio..." : "In approvazione"}</div>}
            <div className="flex justify-between items-start"><h3 className="font-bold text-lg text-slate-900 leading-tight">{note.title}</h3><span className={`font-bold text-sm px-2.5 py-1 rounded-lg ${note.price > 0 ? 'bg-indigo-50 text-indigo-700' : 'bg-emerald-50 text-emerald-700'}`}>{note.price > 0 ? `€${note.price}` : 'Gratis'}</span></div>
            <div className="flex justify-between items-end mt-3"><div className="flex items-center gap-2 text-xs font-bold text-slate-400"><span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded-md"><User size={12}/> {note.profiles?.email?.split('@')[0]}</span>{note.academic_year && <span className="hidden sm:inline bg-slate-50 px-2 py-1 rounded-md">{note.academic_year}</span>}</div><div className="flex gap-2">{isAdmin && !note.is_approved && <button onClick={onApprove} className="bg-emerald-100 text-emerald-700 p-1.5 rounded-lg hover:bg-emerald-200 transition"><CheckCircle size={16}/></button>}{(isAdmin || currentUser?.id === note.user_id) && <button onClick={onDelete} className="bg-rose-50 text-rose-500 p-1.5 rounded-lg hover:bg-rose-100 transition"><Trash2 size={16}/></button>}{!note.isOptimistic && <Link to={`/note/${note.id}`} className="bg-slate-900 text-white px-4 py-1.5 rounded-xl text-xs font-bold hover:bg-indigo-600 transition shadow-lg shadow-slate-200 hover:-translate-y-0.5">Apri</Link>}</div></div>
        </div>
    </div>
);

const QuestionCard = ({ question, isAdmin, currentUser, onApprove, onDelete }) => (
    <div className={`bg-white p-5 rounded-2xl border shadow-sm ${question.isOptimistic ? 'opacity-70 animate-pulse' : ''} ${!question.is_approved ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100'}`}>
        {(!question.is_approved || question.isOptimistic) && <div className="text-[10px] font-bold text-amber-600 mb-2 uppercase tracking-wide">{question.isOptimistic ? "Invio..." : "In approvazione"}</div>}
        <div className="flex gap-4"><div className="text-red-500 font-black text-xl bg-red-50 w-10 h-10 rounded-xl flex items-center justify-center shrink-0">?</div><div className="w-full"><p className="font-bold text-slate-800 leading-snug">"{question.question_text}"</p>{(isAdmin || currentUser?.id === question.user_id) && <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-slate-100">{isAdmin && !question.is_approved && <button onClick={onApprove} className="text-emerald-600 text-xs font-bold hover:bg-emerald-50 px-2 py-1 rounded">Approva</button>}<button onClick={onDelete} className="text-rose-500 text-xs font-bold hover:bg-rose-50 px-2 py-1 rounded">Elimina</button></div>}</div></div>
    </div>
);

const ReviewCard = ({review, isAdmin, currentUser, onApprove, onDelete}) => (
    <div className={`bg-white p-5 rounded-2xl border shadow-sm ${review.isOptimistic ? 'opacity-70 animate-pulse' : ''} ${!review.is_approved ? 'border-amber-200 bg-amber-50/30' : 'border-slate-100'}`}>
        {(!review.is_approved || review.isOptimistic) && <div className="text-[10px] font-bold text-amber-600 mb-2 uppercase tracking-wide">{review.isOptimistic ? "Invio..." : "In approvazione"}</div>}
        <div className="flex justify-between items-center mb-2"><div className="flex text-yellow-400">{[...Array(5)].map((_,i)=><Star key={i} size={14} fill={i<review.rating?"currentColor":"none"} className={i<review.rating?"":"text-slate-200"}/>)}</div><span className="text-xs font-bold text-slate-400 bg-slate-50 px-2 py-1 rounded-md">{review.profiles?.email?.split('@')[0]}</span></div><p className="text-sm text-slate-600 italic leading-relaxed">"{review.comment}"</p>{(isAdmin || currentUser?.id === review.user_id) && <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-slate-100/50">{isAdmin && !review.is_approved && <button onClick={onApprove} className="text-emerald-600 text-xs font-bold hover:bg-emerald-50 px-2 py-1 rounded">Approva</button>}<button onClick={onDelete} className="text-rose-500 text-xs font-bold hover:bg-rose-50 px-2 py-1 rounded">Elimina</button></div>}
    </div>
);

export default Subject;